import { Tab } from "@headlessui/react";
import { Account } from "@radixdlt/radix-dapp-toolkit";
import {
  json,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/cloudflare";
import {
  Form,
  useFetcher,
  useLoaderData,
  useRevalidator,
} from "@remix-run/react";
import BigNumber from "bignumber.js";
import { useEffect, useRef, useState } from "react";
import { NumericFormat } from "react-number-format";
import AccountSelector from "~/components/account-selector";
import TransactionButton from "~/components/transaction-button";
import useInterval from "~/hooks/use-interval";
import {
  generateClaimManifest,
  generateStakingManifest,
  generateUnstakingManifest,
} from "~/lib/manifests";
import { useRadix } from "~/lib/rdt/radixProvider";
import {
  classNames,
  HUG_RESOURCE_ADDRESS,
  sHUG_RESOURCE_ADDRESS,
} from "~/lib/util";
import { loader as walletLoader } from "~/routes/wallet.$account";

export const meta: MetaFunction = () => {
  return [
    {
      title: "Stake your $HUG",
      description: "Stake your $HUG to receive more HUGs",
    },
  ];
};

export default function StakingPage() {
  const walletFetcher = useFetcher<typeof walletLoader>();
  const revalidator = useRevalidator();

  const [account, setAccount] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inputAmount, setInputAmount] = useState<string>("");
  const [stakedAmount, setStakedAmount] = useState<string>("");
  const [balance, setBalance] = useState<string>("");
  const [stakedBalance, setStakedBalance] = useState<string>("0");

  const inputRef = useRef<HTMLInputElement>(null);
  const stakingRef = useRef<HTMLInputElement>(null);

  const { sendTransaction } = useRadix();

  function onAccount(account: Account) {
    setAccount(account.address);

    walletFetcher.load(`/wallet/${account.address}`);
    setError("");
  }

  useEffect(() => {
    if (!walletFetcher.data) {
      return;
    }

    setBalance(
      walletFetcher.data?.balances.find(
        (b) => b.address == HUG_RESOURCE_ADDRESS
      )?.amount || "0"
    );
    setStakedBalance(
      walletFetcher.data?.balances.find(
        (b) => b.address == sHUG_RESOURCE_ADDRESS
      )?.amount || "0"
    );
  }, [walletFetcher.data]);

  async function submitAddTransaction(event) {
    if (!account) {
      return;
    }

    event.preventDefault();

    // const $form = event.currentTarget;
    // const formData = new FormData($form);

    const transactionManifest = generateStakingManifest(account, inputAmount);

    const result = await sendTransaction(
      transactionManifest,
      `We love your HUGs ❤️`
    );

    revalidator.revalidate();
    setInputAmount("");
    walletFetcher.load(`/wallet/${account}`);
  }

  async function submitRemoveTransaction(event) {
    if (!account) {
      return;
    }

    event.preventDefault();

    // const $form = event.currentTarget;
    // const formData = new FormData($form);

    const transactionManifest = generateUnstakingManifest(
      account,
      stakedAmount
    );

    const result = await sendTransaction(
      transactionManifest,
      `Enjoy your HUGs 🙏`
    );

    revalidator.revalidate();
    setInputAmount("");
    walletFetcher.load(`/wallet/${account}`);
  }

  return (
    <Form
      method="post"
      className="md:w-4/5 lg:w-3/4 xl:w-1/2 2xl:w-2/5 mx-4 mb-8 md:mx-auto"
    >
      <h1 className="text-4xl font-bold text-left p-8 bangers text-center text-[60px] pb-0 text-white">
        Staking your hugs
      </h1>
      <p className="text-white text-center yaelah mt-8 p-8 pt-0">
        <a
          href="https://howto.hug.meme/proof-of-hug/intro-to-poh"
          target="_blank"
          className="underline"
        >
          Stake your $HUG
        </a>{" "}
        to receive HUG rewards.
      </p>

      <div className="space-y-0 relative pb-[530px]">
        <AccountSelector mode="light" onAccount={onAccount}></AccountSelector>

        <Tab.Group>
          <Tab.List className="flex space-x-0 p-4  bg-white/70 rounded-t-xl bg-orange-900/20">
            <Tab
              key="add"
              className={({ selected }) =>
                classNames(
                  "w-full rounded-l-lg py-2.5 text-sm font-medium leading-5",
                  "ring-white/60 ring-offset-2 text-[#747474] poppins-medium focus:outline-none",
                  selected
                    ? "bg-white  shadow"
                    : "bg-black/[0.1] hover:text-white"
                )
              }
            >
              Add Stake
            </Tab>
            <Tab
              key="remove"
              className={({ selected }) =>
                classNames(
                  "w-full rounded-r-lg py-2.5 text-sm font-medium leading-5",
                  "ring-white/60 text-[#747474] poppins-medium ring-offset-2  focus:outline-none ",
                  selected
                    ? "bg-white shadow"
                    : "bg-black/[0.1] hover:text-white"
                )
              }
            >
              Remove Stake
            </Tab>
          </Tab.List>
          <Tab.Panels>
            <Tab.Panel key="add">
              <section className="flex flex-col rounded-b-lg shadow-xl px-6 py-4 transition duration-500 cursor-pointer object-cover  bg-white/70">
                <div className="flex justify-end items-top pb-3">
                  {walletFetcher.data ? (
                    <span className="flex flex-row space-x-2 justify-right items-center text-sm font-normal text-[#1D8985] truncate">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-3 h-3 mr-1"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3"
                        />
                      </svg>
                      {(
                        Math.round(
                          (walletFetcher.data.balances.filter(
                            (b) => b.address == HUG_RESOURCE_ADDRESS
                          )[0]?.amount || 0) * 1000000
                        ) / 1000000
                      ).toLocaleString()}{" "}
                      HUG
                    </span>
                  ) : null}
                </div>
                <div className="flex flex-wrap justify-left items-center">
                  <div className="grow">
                    <span className="flex items-center">
                      <img
                        src="https://hug.meme/hug.png"
                        alt=""
                        className="flex-shrink-0 h-12 w-12 rounded-full"
                      />
                    </span>
                  </div>

                  <NumericFormat
                    getInputRef={inputRef}
                    id="inputAmount"
                    name="inputAmount"
                    value={inputAmount}
                    valueIsNumericString={true}
                    onValueChange={(values, sourceInfo) => {
                      setInputAmount(values.value);
                    }}
                    allowLeadingZeros
                    thousandSeparator=","
                    placeholder="1,000,000"
                    className="appearance-none  grow max-w-1/2 max-h-fit lg:ml-5 mt-5 lg:mt-0 p-0 text-[40px] text-left lg:text-right lining-nums bg-transparent border-none text-[#1D8985] poppins-medium focus:outline-none focus:ring-transparent focus:border-transparent"
                  />

                  <span className="ml-3 block truncate font-light text-black poppins-medium text-[40px]">
                    $HUG
                  </span>
                </div>
                <p className="mt-6 text-right text-sm font-normal text-[#1D8985]">
                  {Math.round((+inputAmount / +balance) * 100)}%
                </p>
                <input
                  id="steps-range"
                  type="range"
                  min="0"
                  max="1"
                  value={
                    new BigNumber(inputAmount).div(balance).toNumber() || 0
                  }
                  onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                    setInputAmount(
                      new BigNumber(balance)
                        .times((evt.target as HTMLInputElement).value)
                        .toFixed(18)
                    )
                  }
                  step="0.01"
                  className="w-full h-3 mt-2 mb-3 bg-[#1D8985] rounded-lg appearance-none cursor-pointer ring-[#1D8985]"
                />
              </section>

              <TransactionButton
                title="❤️ Stake HUG"
                onClick={submitAddTransaction}
              />
            </Tab.Panel>

            <Tab.Panel key="remove">
              <section className="flex flex-col rounded-lg shadow-xl px-6 py-4 transition duration-500 cursor-pointer object-cover  bg-white/70">
                <div className="flex justify-end items-top pb-3">
                  {walletFetcher.data ? (
                    <span className="flex flex-row space-x-2 justify-right items-center text-sm font-normal text-[#1D8985] truncate">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-3 h-3 mr-1"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M21 12a2.25 2.25 0 00-2.25-2.25H15a3 3 0 11-6 0H5.25A2.25 2.25 0 003 12m18 0v6a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 18v-6m18 0V9M3 12V9m18 0a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 9m18 0V6a2.25 2.25 0 00-2.25-2.25H5.25A2.25 2.25 0 003 6v3"
                        />
                      </svg>
                      {(
                        Math.round(
                          (walletFetcher.data.balances.find(
                            (b) => b.address == sHUG_RESOURCE_ADDRESS
                          )?.amount || 0) * 1000000
                        ) / 1000000
                      ).toLocaleString()}{" "}
                      sHUG
                    </span>
                  ) : null}
                </div>
                <div className="flex flex-wrap justify-left items-center">
                  <div className="grow">
                    <span className="flex items-center">
                      <img
                        src="https://hug.meme/assets/staking/sHUG.png"
                        alt=""
                        className="flex-shrink-0 h-12 w-12 rounded-full"
                      />
                    </span>
                  </div>

                  <NumericFormat
                    getInputRef={stakingRef}
                    id="inputAmount"
                    name="inputAmount"
                    value={stakedAmount}
                    valueIsNumericString={true}
                    onValueChange={(values, sourceInfo) => {
                      setStakedAmount(values.value);
                    }}
                    allowLeadingZeros
                    thousandSeparator=","
                    placeholder="1,000,000"
                    className="appearance-none  grow max-w-1/2 max-h-fit lg:ml-5 mt-5 lg:mt-0 p-0 text-[40px] text-left lg:text-right lining-nums bg-transparent border-none text-[#1D8985] poppins-medium focus:outline-none focus:ring-transparent focus:border-transparent"
                  />

                  <span className="ml-3 block truncate font-light text-black poppins-medium text-[40px]">
                    $sHUG
                  </span>
                </div>
                <p className="mt-6 text-right text-sm font-normal text-[#1D8985]">
                  {Math.round((+stakedAmount / +stakedBalance) * 100)}%
                </p>
                <input
                  id="steps-range"
                  type="range"
                  min="0"
                  max="1"
                  value={
                    new BigNumber(stakedAmount).div(stakedBalance).toNumber() ||
                    0
                  }
                  onChange={(evt: React.ChangeEvent<HTMLInputElement>) =>
                    setStakedAmount(
                      new BigNumber(stakedBalance)
                        .times((evt.target as HTMLInputElement).value)
                        .toFixed(18)
                    )
                  }
                  step="0.01"
                  className="w-full h-3 mt-2 mb-3 bg-[#1D8985] rounded-lg appearance-none cursor-pointer text-[#1D8985] ring-[#1D8985]"
                />
              </section>

              <TransactionButton
                title="👋 Remove staked HUG"
                onClick={submitRemoveTransaction}
              />
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>

        {error ? (
          <p className="p-4 rounded-lg bg-orange-800 text-white text-center">
            {error}
          </p>
        ) : null}

        <img
          src="/images/celeb.png"
          className="absolute -bottom-10 left-0 right-0 mx-auto"
        />
      </div>
    </Form>
  );
}
